import React from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  Collapse,
  Box,
  Typography,
  Paper,
  Container,
  IconButton,
  Tooltip} from '@mui/material';
import {
  ErrorOutline,
  BugReport,
  ContentCopy,
  Check,
  Close} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { reportClientError } from '@/services/errorReportingService';
import { copyText, formatErrorForPrompt } from '@/utils/errorReport';

/**
 * "Copy for the agent": the whole crash as paste-ready text — user and role, URL,
 * build, and both stacks.
 *
 * Deliberately available in production, where the detail blocks below are hidden:
 * that is exactly when someone needs to hand the crash to a developer or an agent,
 * and the alternative is asking them to open the browser console.
 */
const CopyForAgentButton: React.FC<{
  error: Error;
  componentStack?: string;
  variant?: string;
  size?: 'small' | 'medium';
}> = ({ error, componentStack, variant, size = 'medium' }) => {
  const { t } = useTranslation(['common'], { useSuspense: false });
  const [copied, setCopied] = React.useState(false);

  const copy = async () => {
    const ok = await copyText(formatErrorForPrompt({
      message: error.message ? `${error.name}: ${error.message}` : error.name,
      stack: error.stack,
      componentStack,
      variant,
      source: 'user',
    }));
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <Button
      variant="outlined"
      size={size}
      onClick={copy}
      startIcon={copied ? <Check /> : <ContentCopy />}
      color={copied ? 'success' : 'inherit'}
    >
      {copied
        ? t('error.copied', 'Copiado')
        : t('error.copyForAgent', 'Copiar informe')}
    </Button>
  );
};

export interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  variant?: 'component' | 'route' | 'global';
}

const ComponentFallback: React.FC<{ error: Error; resetError: () => void; componentStack?: string }> = ({ error, resetError, componentStack }) => {
  const { t } = useTranslation(['common'], { useSuspense: false });
  
  return (
    <Alert
      severity="error"
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CopyForAgentButton error={error} componentStack={componentStack}
            variant="component" size="small" />
          <Tooltip title={t('error.dismissError')}>
            <IconButton color="inherit" size="small" onClick={resetError}>
              <Close />
            </IconButton>
          </Tooltip>
        </Box>
      }
      sx={{ mb: 2 }}
    >
      <AlertTitle>{t('error.componentError')}</AlertTitle>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {t('error.somethingWentWrong')}
      </Typography>
      {process.env.NODE_ENV === 'development' && (
        <Collapse in={true}>
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" component="pre" sx={{
              backgroundColor: 'grey.100',
              p: 1,
              borderRadius: 1,
              fontSize: '0.75rem',
              overflow: 'auto',
              maxHeight: 200
            }}>
              {error.message}
              {error.stack}
            </Typography>
          </Box>
        </Collapse>
      )}
    </Alert>
  );
};

const RouteFallback: React.FC<{ error: Error; resetError: () => void; componentStack?: string }> = ({ error, resetError, componentStack }) => {
  const { t } = useTranslation(['common'], { useSuspense: false });
  
  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <ErrorOutline sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          {t('error.pageLoadError')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('error.couldntLoadPage')}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3, flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={() => window.location.href = '/'}>
            {t('error.goToDashboard')}
          </Button>
          <Button variant="outlined" onClick={() => window.location.reload()}>
            {t('error.reloadPage')}
          </Button>
          <CopyForAgentButton error={error} componentStack={componentStack}
            variant="route" />
          <Button variant="text" onClick={resetError} startIcon={<Close />}>
            {t('error.dismiss')}
          </Button>
        </Box>
        
        {process.env.NODE_ENV === 'development' && (
          <Collapse in={true}>
            <Box sx={{ textAlign: 'left', mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t('error.errorDetails')}
              </Typography>
              <Typography variant="caption" component="pre" sx={{
                backgroundColor: 'grey.100',
                p: 2,
                borderRadius: 1,
                fontSize: '0.75rem',
                overflow: 'auto',
                maxHeight: 300
              }}>
                {t('error.error')}: {error.message}
                {error.stack}
              </Typography>
            </Box>
          </Collapse>
        )}
      </Paper>
    </Container>
  );
};

const GlobalFallback: React.FC<{ error: Error; resetError: () => void; componentStack?: string }> = ({ error, resetError, componentStack }) => {
  const { t } = useTranslation(['common'], { useSuspense: false });
  const [reported, setReported] = React.useState(false);

  const reportError = async () => {
    // User-initiated report (the crash was already auto-captured on catch; this
    // records explicit user intent as a separate 'user' source).
    await reportClientError({
      message: error.message,
      stack: error.stack,
      component_stack: componentStack,
      variant: 'global',
      source: 'user',
    });
    setReported(true);
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <ErrorOutline sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          {t('error.applicationError')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('error.unexpectedError')}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3, flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={() => window.location.reload()}>
            {t('error.reloadApplication')}
          </Button>
          <Button variant="outlined" onClick={reportError} startIcon={<BugReport />} disabled={reported}>
            {reported ? t('error.errorReported', 'Reported — thank you') : t('error.reportError')}
          </Button>
          <CopyForAgentButton error={error} componentStack={componentStack}
            variant="global" />
          <Button variant="text" onClick={resetError} startIcon={<Close />}>
            {t('error.dismiss')}
          </Button>
        </Box>

        {process.env.NODE_ENV === 'development' && (
          <Collapse in={true}>
            <Box sx={{ textAlign: 'left', mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Error Details
              </Typography>
              <Typography variant="caption" component="pre" sx={{
                backgroundColor: 'grey.100',
                p: 2,
                borderRadius: 1,
                fontSize: '0.75rem',
                overflow: 'auto',
                maxHeight: 300
              }}>
                {error.message}
                {error.stack}
              </Typography>
            </Box>
          </Collapse>
        )}
      </Paper>
    </Container>
  );
};

class ErrorBoundaryComponent extends React.Component<ErrorBoundaryProps, { hasError: boolean; error: Error | null; componentStack?: string }> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): { hasError: boolean; error: Error | null } {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error Boundary caught an error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      variant: this.props.variant
    });

    this.setState({ componentStack: errorInfo.componentStack || undefined });

    // Call custom error handler
    this.props.onError?.(error, errorInfo);

    // Auto-capture EVERY crash to the backend sink (not just when the user
    // clicks "report"). Fire-and-forget; the service never throws. This is what
    // makes production crashes — especially viewer/operator ones — visible.
    void reportClientError({
      message: error.message,
      stack: error.stack,
      component_stack: errorInfo.componentStack || undefined,
      variant: this.props.variant,
      source: 'auto',
    });
  }

  handleResetError = () => {
    this.setState({ hasError: false, error: null, componentStack: undefined });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      const FallbackComponent = this.props.fallback || (() => {
        switch (this.props.variant) {
          case 'route':
            return RouteFallback;
          case 'global':
            return GlobalFallback;
          default:
            return ComponentFallback;
        }
      })();

      return (
        <FallbackComponent
          error={this.state.error}
          resetError={this.handleResetError}
          componentStack={this.state.componentStack}
        />
      );
    }

    return this.props.children;
  }
}

// Named exports for different variants
export const ComponentErrorBoundary: React.FC<Omit<ErrorBoundaryProps, 'variant'>> = (props) => (
  <ErrorBoundaryComponent {...props} variant="component" />
);

export const RouteErrorBoundary: React.FC<Omit<ErrorBoundaryProps, 'variant'>> = (props) => (
  <ErrorBoundaryComponent {...props} variant="route" />
);

export const GlobalErrorBoundary: React.FC<Omit<ErrorBoundaryProps, 'variant'>> = (props) => (
  <ErrorBoundaryComponent {...props} variant="global" />
);

// Default export
export default ErrorBoundaryComponent;